import { Link } from 'found';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, FormattedPlural, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';
import {
  Alert, Button, Card, CardBody, CardFooter, Col,
  DropdownMenu, DropdownToggle, Row, UncontrolledButtonDropdown,
} from 'reactstrap';

import Loading from 'src/components/Loading';
import {
  getTradeRulesAcceptedForProperty,
} from 'src/components/TradeRule/helpers';
import LinkInI18nBlock from 'src/enosikit/components/LinkInI18nBlock';
import { emptyTradeRulesClassName, prioritizeTradesClassName } from 'src/helpers/tradeHelpers';
import CloseNominatedTradeMutation from 'src/mutations/CloseNominatedTradeMutation';
import FlashesStore from 'src/stores/FlashesStore';
import {
  DIRECTIONS,
  NOMINATED, TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL,
  TRADE_RULE_CANCEL, TRADE_RULE_CLOSE, TRADE_RULE_STATE_ACCEPTED,
  TRADE_RULE_STATE_CANCELLED, TRADE_RULE_STATE_CLOSED,
  TRADE_RULE_STATE_PROPOSED, TRADE_RULE_STATE_REJECTED,
  TRADE_TYPE_NOMINATED,
} from 'src/util/constants';
import isActive from 'src/util/isActive';

import TradeRule from './TradeRule';

const NominatedTradeRulesFragment = graphql`
  fragment NominatedTradeRules_property on Property {
    uuid
    meters {
      edges {
        node {
          uuid
          identifier
          title
          tradePointId
          active { start finish }
          nominatedRules: rules(first: 500, type: TRADE_TYPE_NOMINATED, state: TRADE_RULE_STATE_ACCEPTED) {
            edges {
              node {
                id
                uuid
                priority
                tradeType
                state
                buyer {
                  userId
                  communityId
                  residualId
                  tradePoint {
                    uuid
                    meter {
                      uuid
                      identifier
                      title
                      property {
                        uuid
                        title
                      }
                    }
                  }
                }
                seller {
                  userId
                  communityId
                  residualId
                  tradePoint {
                    uuid
                    meter {
                      uuid
                      identifier
                      title
                      property {
                        uuid
                        title
                      }
                    }
                  }
                }
                clauses {
                  edges {
                    node {
                      price
                      uuid
                      timezone
                      ignoreDaylightSavings
                      ignorePublicHolidays
                      monthsOfYear
                      daysOfWeek
                      timesOfDay {
                        start  { hours minutes seconds }
                        finish { hours minutes seconds }
                      }
                    }
                  }
                }
                start
                finish
                proposedAt
                proposedBy {
                  uuid
                  email
                  givenName
                  familyName
                }
                acceptedAt
                acceptedBy {
                  uuid
                  email
                  givenName
                  familyName
                }
              }
            }
          }
        }
      }
    }
  }
`;

const mutationName = 'closeNominatedTrade';

/**
 * Builds a comparator function for sorting trade rules in a consistent, predictable order.
 *
 * The sorting hierarchy prioritizes:
 * 1. Meter title (alphabetical, null/undefined titles sort last)
 * 2. Priority (ascending, null/undefined treated as 0)
 * 3. Accepted timestamp (ascending, null/undefined treated as 0)
 *
 * This ensures trade rules are displayed in a logical order for users, with meter-based
 * grouping taking precedence over business priority and acceptance timing.
 * @param {object|null|undefined} ruleA - First trade rule to compare.
 * @param {object|null|undefined} ruleB - Second trade rule to compare.
 * @returns {number} Comparison result: -1, 0, or 1 for Array.sort()
 * @example
 * // Sort an array of trade rules
 * const tradeRules = [
 *   {
 *     buyer: { tradePoint: { meter: { title: "Solar Panel Meter" } } },
 *     priority: 2,
 *     acceptedAt: 1640995200
 *   },
 *   {
 *     buyer: { tradePoint: { meter: { title: "Battery Meter" } } },
 *     priority: 1,
 *     acceptedAt: 1640995100
 *   },
 *   {
 *     buyer: { tradePoint: { meter: null } },
 *     priority: 3,
 *     acceptedAt: 1640995300
 *   }
 * ];
 *
 * const sortedRules = tradeRules.sort(buildTradeRuleComparator);
 * // Result: Battery Meter (priority 1), Solar Panel Meter (priority 2), null meter (priority 3)
 */
const buildTradeRuleComparator = (ruleA, ruleB) => {
  // Handle null/undefined rules - null rules sort to the end
  if (!ruleA && !ruleB) return 0;
  if (!ruleA) return 1;
  if (!ruleB) return -1;

  // Handle null/undefined meter or meter.title - rules without titles sort to the end
  const titleA = ruleA?.meter?.title;
  const titleB = ruleB?.meter?.title;

  if (!titleA && !titleB) {
    // Both have no title, continue to priority comparison
  } else if (!titleA) {
    return 1; // ruleA sorts after ruleB
  } else if (!titleB) {
    return -1; // ruleA sorts before ruleB
  } else if (titleA !== titleB) {
    return titleA.localeCompare(titleB);
  }

  // Handle null/undefined priority - treat as 0 priority
  const priorityA = ruleA?.priority ?? 0;
  const priorityB = ruleB?.priority ?? 0;

  if (priorityA !== priorityB) {
    return priorityA - priorityB;
  }

  // Handle null/undefined acceptedAt - treat as 0 timestamp
  const acceptedAtA = ruleA?.acceptedAt ?? 0;
  const acceptedAtB = ruleB?.acceptedAt ?? 0;

  return acceptedAtA - acceptedAtB;
};

/**
 * Sorts trade rules by buy/sell direction using a consistent, predictable order.
 * @param {object|null|undefined} rules - Object containing trade rule arrays to sort.
 * @param {object[]} [rules.sell] - Array of sell trade rules to sort. Can be null/undefined.
 * @param {object[]} [rules.buy] - Array of buy trade rules to sort. Can be null/undefined.
 * @returns {object} Object with sorted sell and buy arrays: { sell: object[], buy: object[] }
 * @example
 * // Sort trade rules for a property
 * const tradeRules = {
 *   sell: [
 *     {
 *       meter: { title: "Solar Panel Meter" },
 *       priority: 2,
 *       acceptedAt: 1640995200,
 *       seller: { tradePoint: { uuid: "seller-1" } }
 *     },
 *     {
 *       meter: { title: "Battery Meter" },
 *       priority: 1,
 *       acceptedAt: 1640995100,
 *       seller: { tradePoint: { uuid: "seller-2" } }
 *     }
 *   ],
 *   buy: [
 *     {
 *       meter: { title: "Wind Turbine Meter" },
 *       priority: 3,
 *       acceptedAt: 1640995300,
 *       buyer: { tradePoint: { uuid: "buyer-1" } }
 *     }
 *   ]
 * };
 *
 * const sortedRules = getSortedTradeRules(tradeRules);
 * // Result: {
 * //   sell: [Battery Meter (priority 1), Solar Panel Meter (priority 2)],
 * //   buy: [Wind Turbine Meter (priority 3)]
 * // }
 *
 * // Handle edge cases
 * const emptyResult = getSortedTradeRules(null);
 * // Result: { sell: [], buy: [] }
 *
 * const partialRules = getSortedTradeRules({ sell: [rule1, rule2] });
 * // Result: { sell: [sorted rules], buy: [] }
 * @see buildTradeRuleComparator - The comparator function used for sorting trade rules
 */
export const getSortedTradeRules = (rules) => {
  // Handle null/undefined rules parameter
  if (!rules || typeof rules !== 'object') {
    return {
      sell: [],
      buy: [],
    };
  }
  const sortedRules = {};

  DIRECTIONS.forEach((dir) => {
    sortedRules[dir] = Array.isArray(rules[dir])
      ? [...rules[dir]].sort(buildTradeRuleComparator) : [];
  });

  return sortedRules;
};

/**
 * Handles failure responses from nominated trade rule mutations by displaying error
 * messages to the user.
 * @param {Error|string} error - Error object or message from the failed mutation.
 */
function handleNominatedTradeMutationFailure(error) {
  FlashesStore.flash(FlashesStore.ERROR, error, mutationName);
}

/**
 * Handles successful responses from closing nominated trade rule mutations by displaying success
 * messages.
 * @param {import('react-intl').IntlShape} intl - the intl object
 * @param {object} response - GraphQL mutation response object
 * @param {object} response.closeNominatedTrade - Nested response containing closed trade rule data
 * @param {string} response.closeNominatedTrade.uuid - UUID of the successfully closed trade rule
 */
function handleCloseNominatedTradeMutationSuccess(intl, response) {
  const { uuid } = response.closeNominatedTrade;
  const successMsg = intl.formatMessage({ id: 'trade_rule.trade_rule_active.nominated_trade_rules.response_message.success', defaultMessage: 'Closed peer-to-peer trade rule with id: {uuid}.' }, { uuid });

  FlashesStore.flash(
    FlashesStore.SUCCESS,
    successMsg,
    mutationName,
  );
}

/**
 * Executes trade rule actions based on the specified action type for nominated trade rules.
 *
 * This function serves as a dispatcher for different trade rule operations, currently supporting
 * the CLOSE action for nominated trade rules. It handles the business logic for determining
 * which mutation to execute and provides appropriate error handling for unsupported actions.
 * @param {import('react-intl').IntlShape} intl - the intl object
 * @param {object} rule - TradeRule object from syndesi.graphql schema
 * @param {string} rule.id - GraphQL relay ID of the trade rule
 * @param {string} rule.uuid - UUID identifier of the trade rule
 * @param {string} action - Action constant defining the operation to perform
 * (e.g., TRADE_RULE_CLOSE)
 * @returns {void|Promise} Returns a Promise if the action is supported and executed, otherwise
 * @example
 * const rule = { id: 'relay-id-123', uuid: 'rule-uuid-456' };
 * tradeRuleAction(rule, TRADE_RULE_CLOSE, intl);
 * // Executes CloseNominatedTradeMutation with success/error callbacks
 * @example
 * tradeRuleAction(rule, 'UNSUPPORTED_ACTION', intl);
 * // Displays error message: "Action UNSUPPORTED_ACTION is not possible."
 */
function tradeRuleAction(intl, rule, action) {
  const { id, uuid } = rule;
  const tradeRuleIdSet = { id, uuid };
  const errorMsg = intl.formatMessage({ id: 'trade_rule.trade_rule_active.nominated_trade_rules.response_message.error', defaultMessage: 'Action {action} is not possible.' }, { action });

  if (action === TRADE_RULE_CLOSE) {
    return CloseNominatedTradeMutation(
      tradeRuleIdSet,
      (response) => handleCloseNominatedTradeMutationSuccess(intl, response),
      handleNominatedTradeMutationFailure,
    );
  }

  return FlashesStore.flash(
    FlashesStore.ERROR,
    errorMsg,
    mutationName,
  );
}

/**
 * Generates appropriate action buttons for trade rules based on their current state and user
 * permissions.
 *
 * This function determines what actions are available for a given trade rule and returns the
 * corresponding UI button component. It handles different trade rule states (proposed, accepted,
 * cancelled, etc.) and validates user permissions based on trade point ownership.
 * @param {import('react-intl').IntlShape} intl - the intl object
 * @param {object} rule - TradeRule object from syndesi.graphql schema with associated meter data
 * @param {string} rule.state - Current state of the trade rule (TradeRuleState enum)
 * @param {object} rule.meter - Meter object associated with this trade rule
 * @param {string} rule.meter.tradePointId - Trade point ID of the meter
 * @param {object} rule.buyer - Trader object representing the buyer
 * @param {object} rule.buyer.tradePoint - TradePoint object for the buyer
 * @param {string} rule.buyer.tradePoint.uuid - UUID of buyer's trade point
 * @param {object} rule.seller - Trader object representing the seller
 * @param {object} rule.seller.tradePoint - TradePoint object for the seller
 * @param {string} rule.seller.tradePoint.uuid - UUID of seller's trade point
 * @returns {React.ReactElement|null} Button component for the appropriate action, or null
 * if no action is available
 */
export function tradeRuleActionButton(intl, rule) {
  const {
    state, meter, buyer, seller,
  } = rule;

  if ([
    TRADE_RULE_STATE_PROPOSED,
    TRADE_RULE_STATE_CANCELLED,
    TRADE_RULE_STATE_REJECTED,
    TRADE_RULE_STATE_CLOSED,
  ].includes(state)) {
    return null;
  }

  if (state === TRADE_RULE_STATE_ACCEPTED) {
    return (
      <Button onClick={() => tradeRuleAction(intl, rule, TRADE_RULE_CLOSE)}>
        {intl.formatMessage({ id: 'trade_rule.trade_rule_active.nominated_trade_rules.close_agreed_trade.label', defaultMessage: 'Close agreed trade' })}
      </Button>
    );
  }

  if (
    [buyer?.tradePoint?.uuid, seller?.tradePoint?.uuid].includes(meter?.tradePointId)
  ) {
    return (
      <Button onClick={() => tradeRuleAction(intl, rule, TRADE_RULE_CANCEL)}>
        {intl.formatMessage({ id: 'trade_rule.trade_rule_active.nominated_trade_rules.cancel_agreed_trade.label', defaultMessage: 'Cancel agreed trade' })}
      </Button>
    );
  }

  return (
    <Alert color="danger">
      {intl.formatMessage({
        id: 'trade_rule.trade_rule_active.nominated_trade_rules.unknown_trade_point.alert',
        defaultMessage: 'Unknown trade point: {meterId} when buyer is {buyerTradePointId} and seller is {sellerTradePointId}',
      }, {
        meterId: meter.uuid,
        buyerTradePointId: buyer.tradePoint.uuid,
        sellerTradePointId: seller.tradePoint.uuid,
      })}
    </Alert>
  );
}

/**
 * NominatedCard displays peer-to-peer trade rules for a property, showing both buying and selling
 * trade rules with their details, pricing information, and available actions.
 * @param {object} props - Component props
 * @param {object} props.property - Property containing meters and their associated nominated trade
 * rules
 * @returns {React.ReactElement} A Card component containing nominated trade information.
 */
function NominatedTradeRules({ property = null }) {
  const intl = useIntl();

  if (!property) {
    return <Loading />;
  }
  const propertyData = useFragment(NominatedTradeRulesFragment, property);
  const { uuid: propertyId } = propertyData;
  const tradeRules = getTradeRulesAcceptedForProperty(propertyData, TRADE_TYPE_NOMINATED);
  const sortedTradeRules = getSortedTradeRules(tradeRules);
  const noTradeRulesClass = emptyTradeRulesClassName(sortedTradeRules, NOMINATED);
  const prioritizeTradesClass = prioritizeTradesClassName(sortedTradeRules, NOMINATED);
  const sellRulesCount = sortedTradeRules?.sell?.length || 0;
  const buyRulesCount = sortedTradeRules?.buy?.length || 0;

  // Internationalized strings
  const titleText = intl.formatMessage({ id: 'trade_rule.trade_rule_active.nominated_trade_rules.title', defaultMessage: 'Peer-to-Peer Trades' });
  const buyingTitleText = intl.formatMessage({ id: 'trade_rule.trade_rule_active.nominated_trade_rules.buying.title', defaultMessage: 'Buying' });
  const sellingTitleText = intl.formatMessage({ id: 'trade_rule.trade_rule_active.nominated_trade_rules.sell.title', defaultMessage: 'Selling' });
  const viewAllTradesText = intl.formatMessage({ id: 'trade_rule.trade_rule_active.nominated_trade_rules.view_all_trades.link.label', defaultMessage: 'View all trade rules' });
  const proposeNewTradeText = intl.formatMessage({ id: 'trade_rule.trade_rule_active.nominated_trade_rules.propose_new_trade.label', defaultMessage: 'Propose new trade' });
  const managePriorityOrderText = intl.formatMessage({ id: 'trade_rule.trade_rule_active.nominated_trade_rules.manage_priority_order.label', defaultMessage: 'Manage priority order' });
  const singleTrade = intl.formatMessage({ id: 'trade_rule.trade_rule_active.nominated_trade_rules.trade.singular', defaultMessage: 'peer to peer trade rule' });
  const multipleTrade = intl.formatMessage({ id: 'trade_rule.trade_rule_active.nominated_trade_rules.trade.multiple', defaultMessage: 'peer to peer trade rules' });

  return (
    <Card className={`mt-4 mb-4 trade-rules-nominated${noTradeRulesClass}`}>
      <CardBody>
        <h2>{titleText}</h2>
        <p>
          <FormattedMessage
            id="trade_rule.trade_rule_active.nominated_trade_rules.help_text"
            defaultMessage="Enosi is best experienced when you trade energy with the people you know. We call this type of trade a peer-to-peer trade."
            values={{
              link: (chunks) => (
                <LinkInI18nBlock
                  url={intl.formatMessage({ id: 'trade_rule.trade_rule_active.nominated_trade_rules.help_text.url', defaultMessage: 'trade_rule.trade_rule_active.nominated_trade_rules.help_text.url' })}
                  urlKey="trade_rule.trade_rule_active.nominated_trade_rules.help_text.url"
                  chunks={chunks}
                />
              ),
            }}
          />
        </p>
        <Row>
          <Col xs="12" sm="12" md="6">
            <h3>{buyingTitleText}</h3>
            {intl.formatMessage(
              {
                id: 'trade_rule.trade_rule_active.nominated_trade_rules.buy.count_details',
                defaultMessage: 'You have {tradeRulesCount} {tradeRulesCountLabel} to buy energy.',
              },
              {
                tradeRulesCount: <strong>{sortedTradeRules.buy.length}</strong>,
                tradeRulesCountLabel: <FormattedPlural
                  value={buyRulesCount}
                  one={singleTrade}
                  other={multipleTrade}
                />,
              },
            )}
            {sortedTradeRules?.buy?.map((rule) => (
              <TradeRule
                rule={rule}
                tradeDirection={TRADE_DIRECTION_BUY}
                tradeType={TRADE_TYPE_NOMINATED}
                propertyId={propertyId}
                key={rule.uuid}
                actionButtonFn={tradeRuleActionButton}
              />
            ))}
          </Col>
          <Col xs="12" sm="12" md="6">
            <h3>{sellingTitleText}</h3>
            {intl.formatMessage(
              {
                id: 'trade_rule.trade_rule_active.nominated_trade_rules.sell.count_details',
                defaultMessage: 'You have {tradeRulesCount} {tradeRulesCountLabel} to sell energy.',
              },
              {
                tradeRulesCount: <strong>{sellRulesCount}</strong>,
                tradeRulesCountLabel: <FormattedPlural
                  value={sellRulesCount}
                  one={singleTrade}
                  other={multipleTrade}
                />,
              },
            )}

            {sortedTradeRules?.sell?.map((rule) => (
              <TradeRule
                rule={rule}
                tradeDirection={TRADE_DIRECTION_SELL}
                tradeType={TRADE_TYPE_NOMINATED}
                propertyId={propertyId}
                key={rule.uuid}
                actionButtonFn={tradeRuleActionButton}
              />
            ))}
          </Col>
        </Row>
      </CardBody>
      <CardFooter>
        <Link to={`/properties/${propertyData.uuid}/trade-rules`}>
          <Button color="primary" className="me-2">
            {viewAllTradesText}
          </Button>
        </Link>
        <Link
          to={`/properties/${propertyData.uuid}/trade-rules/propose/nominated`}
        >
          <Button color="primary" className="me-2">
            {proposeNewTradeText}
          </Button>
        </Link>
        <UncontrolledButtonDropdown className="me-2">
          <DropdownToggle className={prioritizeTradesClass} caret color="primary">
            {managePriorityOrderText}
          </DropdownToggle>
          <DropdownMenu>
            {propertyData.meters.edges.map((edge) => edge && edge.node && (
              <Link
                to={`/properties/${propertyData.uuid}/meters/${edge.node.uuid}/trade-rules/nominated/prioritize`}
                className={`dropdown-item ${!isActive(edge.node.active) ? 'disabled' : ''}`}
                role="menuitem"
                key={edge.node.uuid}
              >
                {edge.node.title}
              </Link>
            ))}
          </DropdownMenu>
        </UncontrolledButtonDropdown>
      </CardFooter>
    </Card>
  );
}

NominatedTradeRules.propTypes = {
  property: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

export default NominatedTradeRules;
